import React, { useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { FormattedMessage } from '../../util/reactIntl';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchSimilarListings } from '../../ducks/similarListings.duck';

import { ListingCard, NamedLink } from '../../components';

import { getCategoryTrail } from './SectionListingHeader';

import css from './SectionSimilarListings.module.css';

// Cards are a quarter of the row from viewportLarge up, half at tablet widths
// and full width on phones — mirroring the grid below.
const CARD_SIZES = '(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 25vw';

/**
 * The deepest category the listing sits in, as a search query param. That is
 * what "similar" means here — the marketplace has no recommendation data, and
 * guessing at one from price or designer would be a worse answer than the
 * category the lender actually chose.
 */
const getCategorySearch = (publicData, categories) => {
  const trail = getCategoryTrail(publicData, categories);
  const deepest = trail[trail.length - 1];
  return deepest ? { key: `pub_categoryLevel${deepest.level}`, value: deepest.id } : null;
};

/**
 * "You may also like": four other pieces from the same category.
 *
 * Fetched on mount rather than in loadData, so a slow query never delays the
 * listing itself. Renders nothing at all until there is something to show.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing the listing entity being viewed
 * @param {Object} props.config marketplace configuration
 * @returns {JSX.Element|null}
 */
const SectionSimilarListings = props => {
  const dispatch = useDispatch();
  const store = useStore();
  const { listing, config } = props;

  const listingId = listing?.id?.uuid;
  const publicData = listing?.attributes?.publicData;
  const categories = config.categoryConfiguration?.categories;
  const categorySearch = getCategorySearch(publicData, categories);
  const searchKey = categorySearch?.key;
  const searchValue = categorySearch?.value;
  const listingImageConfig = config.layout.listingImage;

  const { forListingId, listingIds, hasMore } = useSelector(state => state.similarListings);

  useEffect(() => {
    if (!listingId || !searchKey) {
      return;
    }
    dispatch(
      fetchSimilarListings({
        listingId,
        queryParams: { [searchKey]: searchValue },
        listingImageConfig,
      })
    );
    // listingImageConfig is a config object that is stable for the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, listingId, searchKey, searchValue]);

  // Results from a previously viewed listing must not leak into this one.
  const ids = forListingId === listingId ? listingIds : [];
  const listings =
    ids.length > 0
      ? getMarketplaceEntities(store.getState(), ids.map(id => ({ id, type: 'listing' })))
      : [];

  if (listings.length === 0) {
    return null;
  }

  return (
    <section className={css.root}>
      <div className={css.header}>
        <h2 className={css.title}>
          <FormattedMessage id="ListingPage.similarListingsTitle" />
        </h2>
        {hasMore ? (
          <NamedLink
            className={css.viewAll}
            name="SearchPage"
            to={{ search: `?${searchKey}=${searchValue}` }}
          >
            <FormattedMessage id="ListingPage.similarListingsViewAll" />
          </NamedLink>
        ) : null}
      </div>
      <div className={css.grid}>
        {listings.map(l => (
          <ListingCard
            key={l.id.uuid}
            listing={l}
            layout="editorial"
            renderSizes={CARD_SIZES}
            showAuthorInfo={false}
          />
        ))}
      </div>
    </section>
  );
};

export default SectionSimilarListings;
